import React, { useEffect, useState } from "react";
import { CaretText } from "./PeakBrandMark";

/** Body copy that "types" itself in, character by character (iOS `OnboardingTypewriterText`). */
export const OnboardingTypewriterText: React.FC<{
  text: string;
  isActive?: boolean;
  className?: string;
  charDelayMs?: number;
  startDelayMs?: number;
}> = ({ text, isActive = true, className = "", charDelayMs = 18, startDelayMs = 600 }) => {
  const [visible, setVisible] = useState(0);

  useEffect(() => {
    setVisible(0);
    if (!isActive) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = (count: number) => {
      if (cancelled) return;
      setVisible(count);
      if (count < text.length) timer = setTimeout(() => tick(count + 1), charDelayMs);
    };
    timer = setTimeout(() => tick(1), startDelayMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text, isActive, charDelayMs, startDelayMs]);

  return (
    <p className={`font-geist text-[15px] ${className}`} aria-label={text}>
      <span aria-hidden="true">{text.slice(0, visible)}</span>
      <span aria-hidden="true" className="text-transparent">{text.slice(visible)}</span>
    </p>
  );
};

/** Pulsing brand "^" (stand-in for iOS `PeakLoader`, matching the app's existing `^` loaders). */
export const PeakLoader: React.FC<{ size?: number; className?: string }> = ({ size = 48, className = "" }) => (
  <span
    className={`font-groteskLogo font-bold leading-none text-peak-brand animate-pulse-custom ${className}`}
    style={{ fontSize: size * 1.6, lineHeight: 1 }}
    aria-hidden="true"
  >
    ^
  </span>
);

/**
 * Full-screen takeover while the final profile write runs: "Loading your
 * PEAK^ experience" + live typewriter status line.
 */
const OnboardingFinishLoading: React.FC<{ status: string | null }> = ({ status }) => {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    const text = status ?? "";
    setTyped("");
    if (!text) return;
    let index = 0;
    const timer = setInterval(() => {
      index += 1;
      setTyped(text.slice(0, index));
      if (index >= text.length) clearInterval(timer);
    }, 22);
    return () => clearInterval(timer);
  }, [status]);

  return (
    <div className="flex min-h-[100dvh] w-full flex-col items-center justify-center bg-peak-bg px-8 text-center" role="status" aria-live="polite">
      <PeakLoader />
      <h2 className="mt-6 font-funnel text-[20px] font-semibold text-peak-text">
        <CaretText text="Loading your PEAK^ experience" />
      </h2>
      <p className="mt-2.5 min-h-[22px] font-geist text-[15px] text-peak-text-secondary">{typed || "\u00a0"}</p>
    </div>
  );
};

export default OnboardingFinishLoading;
